import React from "react";
import { Card, Button, Tag, Avatar } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    MailOutlined,
    PhoneOutlined,
    BellOutlined,
    UserOutlined
} from "@ant-design/icons";
import { cn } from "../../../../utils/cn";
import SOURCES from "../../../../utils/constants";

const EnhancedAlertGridView = ({
    record,
    actions = [],
    onCardClick,
}) => {
    const { ALERT_VIA_EMAIL, ALERT_VIA_SMS, ALERT_VIA_BOTH } = SOURCES;

    // Filter actions based on conditions
    const filteredActions = actions
        .map((action) => {
            if (action.condition && !action.condition(record)) {
                return null;
            }
            return action;
        })
        .filter(Boolean);

    const getAlertViaTag = () => {
        let text = record.alertVia;
        let color = "blue";
        if (record.alertVia === ALERT_VIA_BOTH) {
            text = "Email & SMS";
            color = "purple";
        } else if (record.alertVia === ALERT_VIA_SMS) {
            color = "green";
        }
        return <Tag color={color} className="capitalize">{text}</Tag>;
    };

    return (
        <Card
            hoverable
            className="h-full cursor-pointer relative shadow-sm hover:shadow-md transition-all duration-200 border-gray-100 hover:border-primary/20 "
            onClick={onCardClick}
        >
            <div className="flex flex-col h-full gap-2 sm:gap-3">
                {/* Alert Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3 overflow-hidden">
                        <Avatar
                            size={40}
                            className="flex-shrink-0 font-semibold text-base sm:text-lg !bg-primary sm:w-12 sm:h-12"
                            icon={<BellOutlined />}
                        />
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-sm sm:text-base mb-1 truncate text-primary capitalize">
                                {record.alertName || "N/A"}
                            </h3>
                            <div className="flex items-center text-[10px] sm:text-xs">
                                {getAlertViaTag()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Alert Info */}
                <div className="space-y-1.5 sm:space-y-2 border-b border-gray-50 pb-2 sm:pb-3">
                    {/* Email */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <MailOutlined className="mr-2 flex-shrink-0 text-gray-400" />
                        <span className="truncate">
                            Email: <span className="text-gray-800 font-normal">{record.alertEmail}</span>
                        </span>
                    </div>

                    {/* Mobile */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <PhoneOutlined className="mr-2 flex-shrink-0 text-gray-400" />
                        <span className="truncate">
                            Mobile: <span className="text-gray-800 font-normal">{record.alertSms || 'N/A'}</span>
                        </span>
                    </div>
                </div>

                {/* Action Buttons */}
                {filteredActions.length > 0 && (
                    <div
                        className={cn(
                            "grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 pt-2 sm:pt-3 border-t border-gray-200 mt-2",
                            filteredActions.length === 1 && "flex justify-end"
                        )}
                    >
                        {filteredActions.map((action, idx) => (
                            <Button
                                key={idx}
                                title={action.title ?? action.label}
                                className={cn(
                                    "text-xs sm:text-sm font-medium h-8 sm:h-9 my-0.5 sm:my-1 !border-primary !text-primary hover:!bg-primary hover:!text-white !min-w-full flex justify-center items-center transition-colors duration-150 ease-in-out",
                                    action.className
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(record);
                                }}
                            >
                                {action.icon &&
                                    React.cloneElement(action.icon, { className: "mr-1 sm:mr-2 text-xs sm:text-sm" })}
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default EnhancedAlertGridView;
